import { useState } from "react";
import { Button } from "@design-system/Button/Button";
import {
    currentPlatform,
    formatShortcut,
    type ShortcutPlatform,
} from "@design-system/Shortcut/formatShortcut";
import {
    ShortcutView,
    type ShortcutSize,
} from "@design-system/Shortcut/ShortcutView";
import { ToggleGroup } from "@design-system/ToggleGroup/ToggleGroup";
import { useAnatomyMode } from "./componentAnatomyMode";
import { ShortcutMatrix } from "./KeyboardShortcutsPlaygroundMatrix";
import { BooleanControl, TextControl } from "./playgroundControls";
import { SandboxLayout } from "./SandboxLayout";
import { ShowcasePreview } from "./ShowcasePreview";

const sizes: ShortcutSize[] = ["md", "sm"];

const platforms: ShortcutPlatform[] = ["mac", "windows"];

/**
 * Shortcut sandbox. The platform toggle renders `formatShortcut` output for
 * either platform, which `Shortcut` itself never exposes as a prop.
 */
export const KeyboardShortcutsPlayground = () => {
    const [keys, setKeys] = useState("mod+k");
    const [size, setSize] = useState<ShortcutSize>("md");
    const [platform, setPlatform] = useState<ShortcutPlatform>(currentPlatform);
    const [isInDisabledButton, setIsInDisabledButton] = useState(false);
    const steps = formatShortcut(keys, platform);
    // The x-ray draws the key type of the first key typed in the sandbox.
    const anatomy = useAnatomyMode({
        component: "shortcut",
        variantId: steps.at(0)?.at(0)?.kind ?? "letter",
        sizeId: size,
        isDisabled: isInDisabledButton,
    });
    const liveSummary = `${keys} · ${platform} · ${size}`;
    const shortcut = <ShortcutView steps={steps} size={size} />;

    return (
        <SandboxLayout
            controls={
                <>
                    {anatomy.control}
                    <section>
                        <h2>Keys</h2>
                        <TextControl
                            label="Hotkey string"
                            value={keys}
                            placeholder="mod+k, g>t"
                            onChange={setKeys}
                        />
                    </section>
                    <section>
                        <h2>Size</h2>
                        <ToggleGroup.Root<ShortcutSize>
                            value={size}
                            onValueChange={(nextSize) => {
                                if (nextSize) {
                                    setSize(nextSize);
                                }
                            }}
                        >
                            {sizes.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Platform</h2>
                        <ToggleGroup.Root<ShortcutPlatform>
                            value={platform}
                            onValueChange={(nextPlatform) => {
                                if (nextPlatform) {
                                    setPlatform(nextPlatform);
                                }
                            }}
                        >
                            {platforms.map((option) => (
                                <ToggleGroup.Item key={option} value={option}>
                                    {option}
                                </ToggleGroup.Item>
                            ))}
                        </ToggleGroup.Root>
                    </section>
                    <section>
                        <h2>Features</h2>
                        <BooleanControl
                            label="Inside disabled button"
                            checked={isInDisabledButton}
                            onChange={setIsInDisabledButton}
                        />
                    </section>
                </>
            }
        >
            <ShowcasePreview
                width={anatomy.stageWidth}
                summary={
                    anatomy.isActive ? `x-ray · ${liveSummary}` : liveSummary
                }
                belowStage={<ShortcutMatrix platform={platform} />}
                output={JSON.stringify(
                    { Shortcut: { keys, size }, platform, steps },
                    null,
                    2,
                )}
            >
                {anatomy.view ??
                    (isInDisabledButton ? (
                        <Button
                            variant="secondary"
                            disabled
                            rightIcon={<span data-slot-box>{shortcut}</span>}
                        >
                            Preview
                        </Button>
                    ) : (
                        shortcut
                    ))}
            </ShowcasePreview>
        </SandboxLayout>
    );
};
